/*
<purpose>
  Owns server-side, cursor-paginated active-ticket data for DashboardSeite/active
  list: rows, the next-page cursor, the three per-bucket counts, plus
  loading/error state and an IntersectionObserver sentinelRef for infinite scroll.
  Twin of useArchivedPagination — swaps bucket/sort vocabulary + counts shape.
</purpose>
<dependencies>smarttix.getActivePage (services/api.ts)</dependencies>
<constraints>
  - First page fetched on mount and re-fetched whenever bucket, sort, or any filter
    changes; cursor resets on every such change (no stale append across buckets).
  - loadMore() is a no-op while a fetch is in flight or when no nextCursor exists.
  - Appends de-dupe by uuid so an over-eager sentinel can't double-insert rows.
  - sentinelRef is a callback ref: attaches/detaches cleanly as the sentinel node
    mounts/unmounts (after first-page load); loadMore is read via a ref to avoid
    re-creating the IntersectionObserver on every render.
  - refresh() is a silent background re-fetch of page 1: never clears rows, never
    flips loading. On page-1 only (pagedBeyond1Ref=false) it replaces rows;
    when paged beyond page 1 it updates counts only (so loadMore'd rows survive).
    On error it keeps existing rows silently. Guards on fetchingRef to avoid overlap.
</constraints>
*/
import { useState, useEffect, useCallback, useRef } from 'react';
import { TicketListItem } from '../types';
import { smarttix } from '../services/api';

export type ActiveBucket = 'uploads' | 'offen' | 'genehmigt';
export type ActiveSort = 'uploaded' | 'received' | 'updated' | 'rejections' | 'zone';
export type ActiveDir = 'desc' | 'asc';

export interface ActiveFilters {
  search?: string;
  primaryAxis?: string | null;
  secondaryAxis?: string[];
  from?: string | null;
  to?: string | null;
  uploadedBy?: string;
}

export interface ActiveCounts {
  uploads: number;
  offen: number;
  genehmigt: number;
  // Server-computed subset: active tickets in that bucket carrying a form extraction. Backs the
  // Formular-Excel label so it states a fact about the BUCKET rather than counting whichever rows the
  // browser happens to hold. ⚠ An UPPER BOUND on downloadable rows — the server also skips extractions
  // it cannot render, and reports the exact figure as matched-vs-delivered on the download itself.
  formBearing?: { uploads: number; offen: number; genehmigt: number };
  // The server's batch cap. Read from here, NEVER mirrored as a frontend constant: two homes for one
  // number is how the lever ends up enabled at a size the server refuses.
  formBatchCap?: number;
  // Distinguishes "nothing here has a form yet" from "form recognition was never switched on for this
  // tenant" — a per-tenant flag no SQL sum can see.
  formExtractionEnabled?: boolean;
}

const EMPTY_COUNTS: ActiveCounts = {
  uploads: 0, offen: 0, genehmigt: 0,
  formBearing: { uploads: 0, offen: 0, genehmigt: 0 },
};

export interface UseActivePagination {
  rows: TicketListItem[];
  counts: ActiveCounts;
  loading: boolean;       // first-page fetch (bucket/sort/filter change)
  loadingMore: boolean;   // append fetch (sentinel)
  error: string | null;
  loadMore: () => void;
  // Callback ref: attach to the sentinel <div> at the bottom of the list.
  sentinelRef: (node: HTMLElement | null) => void;
  reset: () => void;
  /** Silent background page-1 refetch: never clears rows, never flips loading.
   *  Updates counts always; replaces rows only when on page 1 (not paged beyond).
   *  On error keeps existing rows silently. No-op if a fetch is already in flight. */
  refresh: () => void;
  removeRow: (uuid: string) => void;
  patchRow: (uuid: string, patch: Partial<TicketListItem>) => void;
}

export function useActivePagination(
  bucket: ActiveBucket,
  sort: ActiveSort,
  filters: ActiveFilters,
  dir: ActiveDir = 'desc',
): UseActivePagination {
  const [rows, setRows] = useState<TicketListItem[]>([]);
  const [counts, setCounts] = useState<ActiveCounts>(EMPTY_COUNTS);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Why: in-flight guard read inside loadMore without re-subscribing the
  // IntersectionObserver effect each render (which would thrash the observer).
  const fetchingRef = useRef(false);
  const cursorRef = useRef<string | null>(null);
  cursorRef.current = nextCursor;
  // Tracks whether the user has scrolled beyond page 1 via loadMore.
  // refresh() uses this to decide whether to replace rows (safe on page 1) or
  // update counts only (rows-replace would discard loadMore'd pages).
  const pagedBeyond1Ref = useRef(false);

  const { search, primaryAxis, secondaryAxis, from, to, uploadedBy } = filters;
  // secondaryAxis is a fresh array each render; key on its content so the first-page
  // effect doesn't re-fire on every parent render.
  const secondaryAxisKey = (secondaryAxis ?? []).join(',');

  const fetchPage = useCallback(async (cursor: string | null) => {
    fetchingRef.current = true;
    if (cursor) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await smarttix.getActivePage({
        bucket,
        sort,
        dir,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: cursor || undefined,
      });
      setNextCursor(res.nextCursor);
      if (res.counts) setCounts(res.counts);
      if (cursor) {
        // Append, de-duping by uuid (sentinel can fire twice before state settles).
        pagedBeyond1Ref.current = true;
        setRows(prev => {
          const seen = new Set(prev.map(r => r.uuid));
          return [...prev, ...res.rows.filter(r => !seen.has(r.uuid))];
        });
      } else {
        pagedBeyond1Ref.current = false;
        setRows(res.rows);
      }
    } catch {
      setError('Fehler beim Laden der aktiven Tickets');
      if (!cursor) setRows([]);
    } finally {
      fetchingRef.current = false;
      if (cursor) setLoadingMore(false); else setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, sort, dir, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy]);

  // First page on mount + on every bucket/sort/dir/filter change (cursor reset to null).
  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (fetchingRef.current) return;
    if (!cursorRef.current) return;
    fetchPage(cursorRef.current);
  }, [fetchPage]);

  const reset = useCallback(() => {
    fetchPage(null);
  }, [fetchPage]);

  // Silent background page-1 refetch — reads filter state from the closure captured
  // in the useCallback dep array (same as fetchPage, so always fresh).
  const refresh = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await smarttix.getActivePage({
        bucket,
        sort,
        dir,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: undefined,
      });
      setCounts(res.counts ?? EMPTY_COUNTS);
      setNextCursor(res.nextCursor);
      // Only replace rows when not paged beyond page 1 — replacing would drop
      // load-more'd rows that the user has scrolled to.
      if (!pagedBeyond1Ref.current) {
        pagedBeyond1Ref.current = false;
        setRows(res.rows);
      }
    } catch {
      // Keep existing rows + counts; do NOT set blocking error or toggle loading.
    } finally {
      fetchingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, sort, dir, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy]);

  const removeRow = useCallback((uuid: string) => {
    setRows(prev => prev.filter(r => r.uuid !== uuid));
  }, []);

  const patchRow = useCallback((uuid: string, patch: Partial<TicketListItem>) => {
    setRows(prev => prev.map(r => r.uuid === uuid ? { ...r, ...patch } : r));
  }, []);

  // ─── IntersectionObserver sentinel ─────────────────────────────────────────
  // Callback ref so the observer (re)attaches the moment the sentinel node mounts
  // or unmounts — a static useRef + mount-only effect would miss it (the sentinel
  // only renders once the first page has rows). loadMore self-guards on cursor.
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMoreRef.current();
    });
    obs.observe(node);
    observerRef.current = obs;
  }, []);

  return { rows, counts, loading, loadingMore, error, loadMore, sentinelRef, reset, refresh, removeRow, patchRow };
}
